import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '../../utils/cn';
import { type VariantProps } from 'class-variance-authority';
import {
  RADIO_BUTTON_STATE,
  radioButtonRootVariants,
  radioButtonWrapperStyle,
  radioButtonLabelContainerStyle,
  radioButtonLabelStyle,
  radioButtonLabelDestructiveStyle,
  radioButtonSupportingTextStyle,
  radioButtonSupportingTextDestructiveStyle,
  radioButtonDotStyle,
  radioButtonLabelWithIconStyle
} from './styles';
import { InfoTooltip, type InfoTooltipProps } from '../Tooltip/InfoTooltip';
import { TOOLTIP_ARROW, TOOLTIP_THEME } from '../Tooltip/styles';

export { RADIO_BUTTON_STATE };
export const RadioGroup = RadioGroupPrimitive.Root;

export interface RadioButtonProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
      'color'
    >,
    VariantProps<typeof radioButtonRootVariants> {
  /**
   * The primary label text for the radio button
   */
  label?: string;
  /**
   * Supporting/helper text displayed below the label
   */
  supportingText?: string;
  /**
   * Info tooltip content to display next to the label
   */
  infoTooltip?: string;
  /**
   * Info tooltip props (theme, arrow, etc.)
   */
  infoTooltipProps?: Omit<InfoTooltipProps, 'content'>;
  /**
   * Data test id for testing
   */
  dataTestId?: string;
  /**
   * Custom className for the radio button root
   */
  className?: string;
}

export const RadioButton = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioButtonProps
>(
  (
    {
      className,
      state = RADIO_BUTTON_STATE.DEFAULT,
      label,
      supportingText,
      infoTooltip,
      infoTooltipProps,
      disabled,
      dataTestId,
      ...props
    },
    ref
  ) => {
    // Determine actual state considering disabled
    const actualState = disabled ? RADIO_BUTTON_STATE.DISABLED : state;
    const isDestructive = state === RADIO_BUTTON_STATE.DESTRUCTIVE;

    // If no label and no supporting text, render radio button only
    if (!label && !supportingText) {
      return (
        <RadioGroupPrimitive.Item
          ref={ref}
          className={cn(
            radioButtonRootVariants({ state: actualState }),
            'h-l w-l', // 16px x 16px
            className
          )}
          disabled={disabled}
          data-testid={dataTestId || 'radio-button'}
          {...props}>
          <RadioGroupPrimitive.Indicator className={cn('flex items-center justify-center h-full w-full')}>
            <div className={cn(radioButtonDotStyle, disabled && '!bg-gray-400')} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      );
    }

    // Render radio button with label and/or supporting text
    const id = React.useId();

    return (
      <label
        htmlFor={id}
        className={cn(
          radioButtonWrapperStyle,
          !label && 'items-center',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}>
        <RadioGroupPrimitive.Item
          ref={ref}
          id={id}
          className={cn(
            radioButtonRootVariants({ state: actualState }),
            'h-l w-l',
            className
          )}
          disabled={disabled}
          data-testid={dataTestId || 'radio-button'}
          {...props}>
          <RadioGroupPrimitive.Indicator className={cn('flex items-center justify-center h-full w-full')}>
            <div className={cn(radioButtonDotStyle, disabled && '!bg-gray-400')} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>

        <div className={radioButtonLabelContainerStyle}>
          {label && (
            <div className={radioButtonLabelWithIconStyle}>
              <span
                className={cn(
                  isDestructive && !disabled
                    ? radioButtonLabelDestructiveStyle
                    : radioButtonLabelStyle
                )}>
                {label}
              </span>
              {infoTooltip && (
                <InfoTooltip
                  content={infoTooltip}
                  arrow={TOOLTIP_ARROW.TOP_CENTER}
                  theme={TOOLTIP_THEME.LIGHT}
                  {...infoTooltipProps}
                />
              )}
            </div>
          )}
          {supportingText && (
            <span
              className={cn(
                isDestructive && !disabled
                  ? radioButtonSupportingTextDestructiveStyle
                  : radioButtonSupportingTextStyle,
                !label && !isDestructive && !disabled && 'text-primary',
                !label && 'text-sm !leading-md'
              )}>
              {supportingText}
            </span>
          )}
        </div>
      </label>
    );
  }
);

RadioButton.displayName = 'RadioButton';
